import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { FaTrash } from "react-icons/fa";
import Notification from "../utility/Notification";
import { addCosts, getCostsByDate, updateCosts, getCosts } from "../utility/EndPointGoogleFirebase";

// Formato riga salvata: "Negozio||NomeTipo: importo"
// Separator principale tra voci: "---"
// Separator interno negozio/tipo: "||"

const ManagementCost = () => {
  const today = dayjs();
  const [selectedDate, setSelectedDate] = useState(today);
  const [rows, setRows] = useState([{ shop: "", name: "", amount: "" }]);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expenses, setExpenses] = useState(null);
  const [monthlyTotal, setMonthlyTotal] = useState(null);
  const [monthlyByShop, setMonthlyByShop] = useState([]);
  const [isLoadingMonthly, setIsLoadingMonthly] = useState(false);

  /**
   * Normalizza il nome del negozio in Title Case.
   * "bar", "BAR", "BaR" → "Bar"
   * "esselunga nord" → "Esselunga Nord"
   */
  const normalizeShop = (shop) => {
    if (!shop || !shop.trim()) return "";
    return shop.trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const handleDayClick = (day) => setSelectedDate(selectedDate.date(day));

  const handleChange = (idx, field, value) => {
    const newRows = [...rows];
    newRows[idx][field] = value;
    setRows(newRows);
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchExpenses = async () => {
    const giorno = selectedDate.date().toString();
    const mese = (selectedDate.month() + 1).toString();
    const anno = selectedDate.year().toString();
    try {
      const existingCost = await getCostsByDate(giorno, mese, anno);
      setExpenses(existingCost || null);
    } catch (error) {
      console.error("Errore recupero spese:", error);
    }
  };

  const fetchMonthlyTotal = async () => {
    setIsLoadingMonthly(true);
    try {
      const mese = (selectedDate.month() + 1).toString();
      const anno = selectedDate.year().toString();
      const costs = await getCosts(null, mese, anno);

      const total = costs.reduce((sum, c) => sum + parseFloat(c.totale || 0), 0);
      setMonthlyTotal(total.toFixed(2));

      const shopMap = {};
      costs.forEach((c) => {
        const items = (c.descrizione || "").split("---").filter(Boolean);
        items.forEach((item) => {
          const { shop, value } = parseItem(item);
          const shopKey = shop || "Varie";
          shopMap[shopKey] = (shopMap[shopKey] || 0) + (parseFloat(value) || 0);
        });
      });

      const sorted = Object.entries(shopMap)
        .map(([shop, total]) => ({ shop, total: parseFloat(total.toFixed(2)) }))
        .sort((a, b) => {
          if (a.shop === "Varie") return 1;
          if (b.shop === "Varie") return -1;
          return a.shop.localeCompare(b.shop);
        });
      setMonthlyByShop(sorted);
    } catch (error) {
      console.error("Errore recupero totale mensile:", error);
    } finally {
      setIsLoadingMonthly(false);
    }
  };

  /**
   * Formato DB per ogni voce:
   *   "Negozio||TipoSpesa: 12.50"
   * Se il negozio è vuoto:
   *   "||TipoSpesa: 12.50"
   */
  const saveExpenses = async () => {
    const validRows = rows.filter((row) => row.name || row.amount);
    if (validRows.length === 0) {
      showNotification("Inserisci almeno una spesa valida.", "error");
      return;
    }
    setIsLoading(true);

    const descrizione = validRows
      .map((row) => {
        const shop = normalizeShop(row.shop); // normalizzazione Title Case
        const name = (row.name || "Spesa").trim();
        const amount = parseFloat(row.amount || 0).toFixed(2);
        return `${shop}||${name}: ${amount}`;
      })
      .join("---");

    const totale = validRows
      .reduce((sum, row) => sum + parseFloat(row.amount || 0), 0)
      .toFixed(2);

    const giorno = selectedDate.date().toString();
    const mese = (selectedDate.month() + 1).toString();
    const anno = selectedDate.year().toString();

    try {
      const existingCost = await getCostsByDate(giorno, mese, anno);
      if (!existingCost) {
        await addCosts(descrizione, totale, giorno, mese, anno);
        showNotification("Spese salvate con successo!", "success");
      } else {
        const updatedDescrizione = `${existingCost.descrizione}---${descrizione}`;
        const updatedTotale = (parseFloat(existingCost.totale) + parseFloat(totale)).toFixed(2);
        await updateCosts(existingCost.id, {
          descrizione: updatedDescrizione,
          totale: parseFloat(updatedTotale),
        });
        showNotification("Spese aggiornate con successo!", "success");
      }
      setRows([{ shop: "", name: "", amount: "" }]);
      fetchExpenses();
      fetchMonthlyTotal();
    } catch (error) {
      showNotification("Errore durante il salvataggio.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Parser di una singola voce DB.
   * Gestisce sia il nuovo formato "Negozio||Tipo: importo"
   * sia il vecchio formato legacy "Tipo: importo" (senza "||").
   * Il nome negozio viene normalizzato in Title Case anche in lettura,
   * così i dati già salvati con case diverso vengono raggruppati correttamente.
   */
  const parseItem = (desc) => {
    if (desc.includes("||")) {
      const [shop, rest] = desc.split("||");
      const colonIdx = rest.lastIndexOf(":");
      const label = rest.slice(0, colonIdx).trim();
      const value = rest.slice(colonIdx + 1).trim();
      return { shop: normalizeShop(shop) || "Varie", label, value };
    }
    // Legacy (vecchio formato senza ||)
    const colonIdx = desc.lastIndexOf(":");
    const label = desc.slice(0, colonIdx).trim();
    const value = desc.slice(colonIdx + 1).trim();
    return { shop: "Varie", label, value };
  };

  const deleteExpenseItem = async (indexToRemove) => {
    if (!expenses) return;
    setIsLoading(true);
    try {
      const items = expenses.descrizione.split("---").filter(Boolean);
      const updatedItems = items.filter((_, i) => i !== indexToRemove);
      if (updatedItems.length === 0) {
        await updateCosts(expenses.id, { descrizione: "", totale: 0 });
        setExpenses(null);
        showNotification("Tutte le spese eliminate.", "success");
      } else {
        const newTotale = updatedItems
          .reduce((sum, item) => {
            const { value } = parseItem(item);
            return sum + (parseFloat(value) || 0);
          }, 0)
          .toFixed(2);
        await updateCosts(expenses.id, {
          descrizione: updatedItems.join("---"),
          totale: parseFloat(newTotale),
        });
        showNotification("Voce eliminata.", "success");
        fetchExpenses();
      }
      fetchMonthlyTotal();
    } catch (error) {
      showNotification("Errore durante l'eliminazione.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchExpenses(); }, [selectedDate]);
  useEffect(() => { fetchMonthlyTotal(); }, [selectedDate.month(), selectedDate.year()]);

  const daysInMonth = selectedDate.daysInMonth();
  const firstDayOfMonth = selectedDate.startOf("month").day();
  const weekDays = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
  const expenseItems = expenses ? expenses.descrizione.split("---").filter(Boolean) : [];

  const sectionCard = {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  };

  const inputStyle = {
    width: "100%",
    padding: "9px 11px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    fontSize: 14,
    color: "#111827",
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    WebkitAppearance: "none",
    appearance: "none",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500&display=swap');
        * { box-sizing: border-box; }

        .mc-loader-ring {
          width: 36px; height: 36px;
          border: 2px solid #e5e7eb; border-top-color: #4f46e5;
          border-radius: 50%;
          animation: mcSpin 0.8s linear infinite;
        }
        @keyframes mcSpin { to { transform: rotate(360deg); } }

        .mc-wrapper {
          max-width: 680px; margin: 0 auto;
          padding: 16px 12px;
          padding-left: max(12px, env(safe-area-inset-left));
          padding-right: max(12px, env(safe-area-inset-right));
          padding-bottom: max(32px, env(safe-area-inset-bottom));
          display: flex; flex-direction: column; gap: 16px;
        }
        @media (min-width: 480px) { .mc-wrapper { padding: 20px 16px; } }
        @media (min-width: 768px) { .mc-wrapper { padding: 32px 24px; } }

        .mc-day-btn {
          width: 100%; aspect-ratio: 1;
          display: flex; align-items: center; justify-content: center;
          border-radius: 8px; border: none;
          font-size: 13px; cursor: pointer;
          background: transparent; color: #374151;
          transition: background 0.12s; font-family: inherit;
          -webkit-tap-highlight-color: transparent;
        }
        .mc-day-btn:hover { background: #f3f4f6; }
        .mc-day-btn.today { background: #eef2ff; color: #4f46e5; font-weight: 500; }
        .mc-day-btn.selected { background: #4f46e5; color: #fff; font-weight: 500; }
        .mc-day-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        .mc-input:focus { border-color: #6366f1 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }

        .mc-add-btn {
          display: flex; align-items: center; gap: 6px;
          padding: 9px 16px; border-radius: 8px;
          border: 1.5px dashed #d1d5db;
          background: transparent; color: #6b7280;
          font-size: 13px; font-weight: 500; cursor: pointer;
          transition: all 0.15s; font-family: inherit;
          -webkit-tap-highlight-color: transparent;
        }
        @media (hover: hover) { .mc-add-btn:hover { border-color: #6366f1; color: #4f46e5; background: #eef2ff; } }

        .mc-save-btn {
          padding: 11px 28px; border-radius: 9px; border: none;
          background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
          color: #fff; font-size: 14px; font-weight: 500;
          cursor: pointer; font-family: inherit;
          transition: transform 0.15s, box-shadow 0.15s;
          -webkit-tap-highlight-color: transparent;
        }
        @media (hover: hover) {
          .mc-save-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(79,70,229,0.28); }
        }
        .mc-save-btn:active { transform: scale(0.98); }
        .mc-save-btn:disabled { background: #e5e7eb; color: #9ca3af; cursor: not-allowed; }

        .mc-del-row-btn {
          display: flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; flex-shrink: 0;
          border-radius: 8px; border: 1px solid #fecaca;
          background: #fff1f2; color: #dc2626;
          font-size: 13px; cursor: pointer; font-family: inherit;
          transition: all 0.15s;
          -webkit-tap-highlight-color: transparent;
        }
        @media (hover: hover) {
          .mc-del-row-btn:hover { background: #fee2e2; border-color: #f87171; box-shadow: 0 2px 6px rgba(220,38,38,0.15); }
        }
        .mc-del-row-btn:active { transform: scale(0.95); }
        .mc-del-row-btn:disabled { opacity: 0.35; cursor: not-allowed; }

        .mc-input-del-btn {
          display: flex; align-items: center; justify-content: center;
          width: 36px; height: 36px; flex-shrink: 0;
          border-radius: 8px; border: 1px solid #e5e7eb;
          background: #f9fafb; color: #9ca3af;
          font-size: 13px; cursor: pointer; font-family: inherit;
          transition: all 0.15s;
          -webkit-tap-highlight-color: transparent;
        }
        @media (hover: hover) {
          .mc-input-del-btn:hover { background: #fee2e2; color: #dc2626; border-color: #fecaca; }
        }

        .mc-table { width: 100%; border-collapse: collapse; }
        .mc-th {
          padding: 9px 14px; text-align: left;
          font-size: 11px; font-weight: 500; color: #9ca3af;
          letter-spacing: 0.06em; text-transform: uppercase;
          background: #f9fafb; border-bottom: 1px solid #e5e7eb;
        }
        .mc-th:last-child { width: 44px; }
        .mc-td {
          padding: 11px 14px;
          font-size: 13px; color: #374151;
          border-bottom: 1px solid #f3f4f6;
          vertical-align: middle;
        }
        .mc-tr:last-child .mc-td { border-bottom: none; }
        .mc-tf {
          padding: 11px 14px;
          font-size: 14px; font-weight: 600; color: #4f46e5;
          background: #f9fafb; border-top: 1px solid #e5e7eb;
        }

        .page-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(22px, 5vw, 34px);
          font-weight: 400; color: #111827; line-height: 1.2;
        }

        .mc-monthly-card {
          background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
          border-radius: 14px;
          border: none;
          box-shadow: 0 4px 16px rgba(79,70,229,0.22);
          padding: 18px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .mc-monthly-shimmer {
          display: inline-block;
          width: 90px; height: 28px;
          border-radius: 6px;
          background: rgba(255,255,255,0.15);
          animation: mcShimmer 1.2s ease-in-out infinite;
        }
        @keyframes mcShimmer {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }

        .mc-shop-badge {
          display: inline-block;
          font-size: 11px; font-weight: 500;
          color: #6366f1;
          background: #eef2ff;
          border: 1px solid #c7d2fe;
          border-radius: 100px;
          padding: 2px 9px;
          line-height: 1.6;
          white-space: nowrap;
        }
        .mc-shop-badge.varie {
          color: #9ca3af;
          background: #f3f4f6;
          border-color: #e5e7eb;
        }

        .mc-shop-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 16px;
          border-bottom: 1px solid #f3f4f6;
          gap: 12px;
        }
        .mc-shop-row:last-child { border-bottom: none; }
        .mc-shop-bar-wrap {
          flex: 1; height: 4px; border-radius: 100px;
          background: #f3f4f6; overflow: hidden; min-width: 40px;
        }
        .mc-shop-bar-fill {
          height: 100%; border-radius: 100px;
          background: linear-gradient(90deg, #4f46e5, #6366f1);
          transition: width 0.4s ease;
        }
        .mc-shop-bar-fill.varie {
          background: linear-gradient(90deg, #9ca3af, #d1d5db);
        }
      `}</style>

      {/* Loading overlay */}
      {isLoading && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 50,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14,
          background: "rgba(249,250,251,0.88)",
          backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        }}>
          <div className="mc-loader-ring" />
          <p style={{ fontSize: 12, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Attendere
          </p>
        </div>
      )}

      <Notification notification={notification} onClose={() => setNotification(null)} />

      <div className="mc-wrapper">

        {/* Page header */}
        <div style={{ textAlign: "center", paddingBottom: 20, borderBottom: "1px solid #e5e7eb" }}>
          <p style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#9ca3af", marginBottom: 6 }}>
            Gestione
          </p>
          <h1 className="page-title">Spese</h1>
          <div style={{ width: 28, height: 2, background: "#4f46e5", borderRadius: 2, margin: "10px auto 0" }} />
        </div>

        {/* Calendar */}
        <div style={sectionCard}>
          <div style={{
            padding: "14px 16px", borderBottom: "1px solid #f3f4f6",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <button
              onClick={() => setSelectedDate((p) => p.subtract(1, "month"))}
              disabled={isLoading}
              style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#f9fafb", color: "#374151", fontSize: 16, cursor: "pointer", fontFamily: "inherit" }}
            >‹</button>
            <span style={{ fontSize: 14, fontWeight: 500, color: "#111827", textTransform: "capitalize" }}>
              {selectedDate.format("MMMM YYYY")}
            </span>
            <button
              onClick={() => setSelectedDate((p) => p.add(1, "month"))}
              disabled={isLoading}
              style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#f9fafb", color: "#374151", fontSize: 16, cursor: "pointer", fontFamily: "inherit" }}
            >›</button>
          </div>
          <div style={{ padding: "12px 14px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>
              {weekDays.map((d) => (
                <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 500, color: "#9ca3af", letterSpacing: "0.04em", padding: "4px 0" }}>
                  {d}
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
              {[...Array(firstDayOfMonth)].map((_, i) => <div key={`e-${i}`} />)}
              {[...Array(daysInMonth)].map((_, i) => {
                const day = i + 1;
                const isToday = selectedDate.date(day).isSame(today, "day");
                const isSelected = selectedDate.date(day).isSame(selectedDate, "day");
                return (
                  <button
                    key={day}
                    onClick={() => handleDayClick(day)}
                    disabled={isLoading}
                    className={`mc-day-btn${isSelected ? " selected" : isToday ? " today" : ""}`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Totale mensile */}
        <div className="mc-monthly-card">
          <div>
            <p style={{
              fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
              color: "rgba(255,255,255,0.65)", margin: 0,
            }}>
              Totale {selectedDate.format("MMMM YYYY")}
            </p>
            {isLoadingMonthly ? (
              <div className="mc-monthly-shimmer" style={{ marginTop: 6 }} />
            ) : (
              <p style={{ fontSize: 26, fontWeight: 700, color: "#fff", margin: "4px 0 0", letterSpacing: "-0.5px" }}>
                {monthlyTotal !== null ? `${monthlyTotal} €` : "— €"}
              </p>
            )}
          </div>
          <div style={{
            width: 48, height: 48, borderRadius: 14, flexShrink: 0,
            background: "rgba(255,255,255,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" fill="none"/>
              <path d="M12 6v1.5M12 16.5V18M8.5 8.5l1.06 1.06M14.44 14.44l1.06 1.06M6 12h1.5M16.5 12H18M8.5 15.5l1.06-1.06M14.44 9.56l1.06-1.06" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" strokeLinecap="round"/>
              <text x="12" y="13" textAnchor="middle" dominantBaseline="central" fill="white" fontSize="9" fontWeight="700" fontFamily="inherit">€</text>
            </svg>
          </div>
        </div>

        {/* New expense form */}
        <div style={sectionCard}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #f3f4f6" }}>
            <h2 style={{ fontSize: 15, fontWeight: 500, color: "#111827", margin: 0 }}>Nuove spese</h2>
            <p style={{ fontSize: 12, color: "#9ca3af", margin: "2px 0 0" }}>
              {selectedDate.format("dddd D MMMM YYYY")}
            </p>
          </div>
          <div style={{ padding: "14px 16px" }}>

            {/* Column labels */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 80px 36px",
              gap: 8,
              marginBottom: 6,
              paddingLeft: 2,
            }}>
              {["Negozio", "Tipo spesa", "Importo (€)", ""].map((label) => (
                <span key={label} style={{ fontSize: 11, color: "#9ca3af", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                  {label}
                </span>
              ))}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {rows.map((row, idx) => (
                <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px 36px", gap: 8, alignItems: "center" }}>
                  {/* Negozio */}
                  <input
                    type="text"
                    placeholder="es. Esselunga"
                    value={row.shop}
                    onChange={(e) => handleChange(idx, "shop", e.target.value)}
                    disabled={isLoading}
                    className="mc-input"
                    style={inputStyle}
                  />
                  {/* Tipo spesa */}
                  <input
                    type="text"
                    placeholder="es. Alimentari"
                    value={row.name}
                    onChange={(e) => handleChange(idx, "name", e.target.value)}
                    disabled={isLoading}
                    className="mc-input"
                    style={inputStyle}
                  />
                  {/* Importo */}
                  <input
                    type="number"
                    placeholder="0.00"
                    value={row.amount}
                    onChange={(e) => handleChange(idx, "amount", e.target.value)}
                    disabled={isLoading}
                    inputMode="decimal"
                    className="mc-input"
                    style={inputStyle}
                  />
                  {/* Delete row */}
                  <button
                    onClick={() => setRows(rows.filter((_, i) => i !== idx))}
                    disabled={isLoading}
                    className="mc-input-del-btn"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <button onClick={() => setRows([...rows, { shop: "", name: "", amount: "" }])} disabled={isLoading} className="mc-add-btn">
                + Aggiungi voce
              </button>
              <button onClick={saveExpenses} disabled={isLoading} className="mc-save-btn">
                Salva Spese
              </button>
            </div>
          </div>
        </div>

        {/* Saved expenses TABLE */}
        {expenseItems.length > 0 && (
          <div style={sectionCard}>
            <div style={{
              padding: "14px 16px", borderBottom: "1px solid #f3f4f6",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
            }}>
              <h2 style={{ fontSize: 15, fontWeight: 500, color: "#111827", margin: 0 }}>
                Spese salvate
              </h2>
              <div style={{
                fontSize: 12, fontWeight: 500, flexShrink: 0,
                background: "#f0fdf4", color: "#15803d",
                padding: "4px 12px", borderRadius: 100,
                border: "1px solid #bbf7d0",
              }}>
                {expenseItems.length} {expenseItems.length === 1 ? "voce" : "voci"}
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table className="mc-table">
                <thead>
                  <tr>
                    <th className="mc-th">#</th>
                    <th className="mc-th">Negozio</th>
                    <th className="mc-th">Descrizione</th>
                    <th className="mc-th" style={{ textAlign: "right" }}>Importo</th>
                    <th className="mc-th" style={{ textAlign: "center" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {expenseItems.map((desc, idx) => {
                    const { shop, label, value } = parseItem(desc);
                    return (
                      <tr key={idx} className="mc-tr">
                        <td className="mc-td" style={{ color: "#9ca3af", width: 36 }}>{idx + 1}</td>
                        <td className="mc-td">
                          <span className={`mc-shop-badge${shop === "Varie" ? " varie" : ""}`}>{shop}</span>
                        </td>
                        <td className="mc-td">{label}</td>
                        <td className="mc-td" style={{ textAlign: "right", fontWeight: 500, color: "#111827", whiteSpace: "nowrap" }}>
                          {value} €
                        </td>
                        <td className="mc-td" style={{ textAlign: "center", width: 44 }}>
                          <button
                            onClick={() => deleteExpenseItem(idx)}
                            disabled={isLoading}
                            className="mc-del-row-btn"
                            title="Elimina voce"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="mc-tf" colSpan={3}>Totale</td>
                    <td className="mc-tf" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {expenses.totale} €
                    </td>
                    <td className="mc-tf" />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Riepilogo mensile per negozio */}
        {monthlyByShop.length > 0 && (
          <div style={sectionCard}>
            <div style={{
              padding: "14px 16px", borderBottom: "1px solid #f3f4f6",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
            }}>
              <div>
                <h2 style={{ fontSize: 15, fontWeight: 500, color: "#111827", margin: 0 }}>
                  Riepilogo per negozio
                </h2>
                <p style={{ fontSize: 12, color: "#9ca3af", margin: "2px 0 0" }}>
                  {selectedDate.format("MMMM YYYY")}
                </p>
              </div>
              {isLoadingMonthly && <div className="mc-loader-ring" style={{ width: 20, height: 20 }} />}
            </div>

            <div>
              {(() => {
                const maxTotal = Math.max(...monthlyByShop.map((s) => s.total), 1);
                return monthlyByShop.map(({ shop, total }) => (
                  <div key={shop} className="mc-shop-row">
                    <span style={{
                      fontSize: 13, fontWeight: 500,
                      color: shop === "Varie" ? "#9ca3af" : "#374151",
                      minWidth: 90, flexShrink: 0,
                    }}>
                      {shop}
                    </span>
                    <div className="mc-shop-bar-wrap">
                      <div
                        className={`mc-shop-bar-fill${shop === "Varie" ? " varie" : ""}`}
                        style={{ width: `${(total / maxTotal) * 100}%` }}
                      />
                    </div>
                    <span style={{
                      fontSize: 13, fontWeight: 600,
                      color: shop === "Varie" ? "#9ca3af" : "#4f46e5",
                      minWidth: 72, textAlign: "right", flexShrink: 0,
                    }}>
                      {total.toFixed(2)} €
                    </span>
                  </div>
                ));
              })()}
            </div>

            {/* Totale generale */}
            <div style={{
              padding: "11px 16px",
              borderTop: "1px solid #e5e7eb",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              background: "#f9fafb", borderRadius: "0 0 14px 14px",
            }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>Totale mese</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#4f46e5" }}>
                {monthlyTotal} €
              </span>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ManagementCost;